/**
 * @file Pagination.tsx
 *
 * Shared pager for the admin list views (Products, Categories, Orders). Renders
 * previous/next controls plus a compact page sequence (with ellipsis gaps) from
 * the shared {@link buildPageItems}, and returns nothing for a single page. The
 * accessible `<nav>` name is supplied by the caller (its section title); the
 * prev/next/page labels are the generic `admin.pagination.*` messages, so the
 * three tabs share one implementation instead of a copy each.
 */

import { Icon } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { buildPageItems } from './build-page-items';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  /** Accessible name for the surrounding `<nav>` (typically the section title). */
  navLabel: string;
  /** Gravity surface class for the pager strip; defaults to `gv-surface-bright`. */
  surfaceClassName?: string;
  /** `data-testid` namespace for this pager instance; defaults to `pagination`. */
  testIdPrefix?: string;
}

/**
 * Renders a list-view pager.
 *
 * @param props.page - The current 1-based page.
 * @param props.totalPages - Total number of pages.
 * @param props.onPage - Called with the page to navigate to.
 * @param props.navLabel - Accessible name for the surrounding `<nav>`.
 * @param props.surfaceClassName - Gravity surface class; defaults to `gv-surface-bright`.
 * @param props.testIdPrefix - `data-testid` namespace; defaults to `pagination`.
 *
 * @returns The pager element, or `null` for a single page.
 */
export function Pagination({
  page,
  totalPages,
  onPage,
  navLabel,
  surfaceClassName = 'gv-surface-bright',
  testIdPrefix = 'pagination',
}: PaginationProps) {
  const intl = useIntl();
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`gv-pagination ${surfaceClassName}`} data-testid={testIdPrefix}>
      <nav aria-label={navLabel}>
        <ul>
          <li className={page <= 1 ? 'gv-disabled' : ''}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-prev`}
              onClick={() => onPage(page - 1)}
              disabled={page <= 1}
              aria-label={intl.formatMessage(adminMessages.paginationPrevPage)}
            >
              <Icon name="keyboard_arrow_left" size="small" />
            </button>
          </li>
          {buildPageItems(page, totalPages).map((item, i) =>
            item === '...' ? (
              <li key={`ellipsis-${i}`} className="gv-disabled" aria-hidden="true">
                <span>
                  <Icon name="more_horiz" size="small" />
                </span>
              </li>
            ) : (
              <li key={item} className={`gv-page${item === page ? ' gv-active' : ''}`}>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-page`}
                  data-page={item}
                  onClick={() => onPage(item)}
                  aria-label={intl.formatMessage(adminMessages.paginationPageLabel, {
                    page: item,
                  })}
                  aria-current={item === page ? 'page' : undefined}
                >
                  {item}
                </button>
              </li>
            ),
          )}
          <li className={page >= totalPages ? 'gv-disabled' : ''}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-next`}
              onClick={() => onPage(page + 1)}
              disabled={page >= totalPages}
              aria-label={intl.formatMessage(adminMessages.paginationNextPage)}
            >
              <Icon name="keyboard_arrow_right" size="small" />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
